import { supabase } from './supabase';
import { isPrivateSession } from './sessionTypeUtils';

/**
 * Whether a session should appear on browse/discovery surfaces (home, find sessions).
 *
 * - Public visibility (`is_public === true`): visible to everyone
 * - Members-only (`is_public === false`): club members + anyone already tied to the session
 * - Private Session type (join-code): excluded from open browse lists
 *
 * @param {Object} session
 * @param {{ memberClubIds?: Set<string>, isCommitted?: boolean }} [viewer]
 */
export function canViewSessionInBrowse(session, { memberClubIds = new Set(), isCommitted = false } = {}) {
  if (!session) return false;
  if (isPrivateSession(session)) return false;
  if (session.is_public === true) return true;
  if (isCommitted) return true;
  if (session.club_id && memberClubIds.has(session.club_id)) return true;
  return false;
}

/**
 * Club memberships + session ids the viewer is already tied to (host, signup, session_players).
 * Used to filter members-only sessions on browse pages.
 *
 * @param {string} userId
 * @returns {Promise<{ memberClubIds: Set<string>, committedSessionIds: Set<string> }>}
 */
export async function fetchViewerSessionBrowseContext(userId) {
  const empty = { memberClubIds: new Set(), committedSessionIds: new Set() };
  if (!userId) return empty;

  const [membershipsRes, signupsRes, hostedRes, sessionPlayersRes] = await Promise.all([
    supabase
      .from('players_club')
      .select('club_id')
      .eq('player_id', userId)
      .eq('is_active', true),
    supabase
      .from('session_signups')
      .select('session_id')
      .eq('player_id', userId)
      .in('signup_status', ['confirmed', 'waitlist']),
    supabase
      .from('sessions')
      .select('id')
      .eq('host_player_id', userId),
    supabase
      .from('session_players')
      .select('session_id')
      .eq('player_id', userId)
      .in('status', ['active', 'signed_up', 'paused']),
  ]);

  if (membershipsRes.error) throw membershipsRes.error;
  if (signupsRes.error) throw signupsRes.error;
  if (hostedRes.error) throw hostedRes.error;
  if (sessionPlayersRes.error) throw sessionPlayersRes.error;

  const memberClubIds = new Set((membershipsRes.data || []).map((row) => row.club_id));
  const committedSessionIds = new Set([
    ...(signupsRes.data || []).map((row) => row.session_id),
    ...(hostedRes.data || []).map((row) => row.id),
    ...(sessionPlayersRes.data || []).map((row) => row.session_id),
  ]);

  return { memberClubIds, committedSessionIds };
}

/**
 * @param {Object} session
 * @param {{ memberClubIds: Set<string>, committedSessionIds: Set<string> }} context
 */
export function canViewSessionInBrowseWithContext(session, { memberClubIds, committedSessionIds }) {
  return canViewSessionInBrowse(session, {
    memberClubIds,
    isCommitted: committedSessionIds.has(session?.id),
  });
}
